/** Story feed composition rules. Free of React and player internals, so tests can drive them. */
import { STORY_CONFIG_ID } from '../../util/util';

export const STORY_AD_ITEM_TYPE = 'ad';
export const STORY_VIDEO_ITEM_TYPE = 'video';

export interface StoryFeedVideoItem {
	type: typeof STORY_VIDEO_ITEM_TYPE;
	key: string;
	media: any;
	/** Index within the playlist's medias, ignoring ad slots. */
	mediaIndex: number;
}

export interface StoryFeedAdItem {
	type: typeof STORY_AD_ITEM_TYPE;
	key: string;
	/** 0-based ordinal of this ad slot in the feed. */
	adSlotIndex: number;
}

export type StoryFeedItem = StoryFeedVideoItem | StoryFeedAdItem;

/** A Story preset playing a playlist - the only thing the reels feed can be built on. */
export const isStoryFeedPreset = (configParams?: any): boolean =>
	!!configParams?.playlistId &&
	typeof configParams?.configId === 'string' &&
	configParams.configId.toLowerCase().includes(STORY_CONFIG_ID.toLowerCase());

/** Opt-in per embed, and only on a Story playlist. Both halves live here; they used to drift. */
export const isReelsFeedEnabled = (configParams?: any): boolean =>
	configParams?.reels === 'on' && isStoryFeedPreset(configParams);

/** Ad slots need a tag, an interval, and the feed. No aspect-ratio test: every clip gets a 9:16 stage. */
export const shouldUseStoryAdSlots = ({
	adTagUrl,
	adInterval,
	configParams,
}: {
	adTagUrl?: string;
	adInterval: number;
	configParams?: any;
}): boolean => {
	// Number.isFinite, not just <= 0: NaN fails every comparison, so a non-numeric interval would
	// report slots enabled while none are built - which disables GoBrain's ads and serves none at all.
	if (!adTagUrl || !Number.isFinite(adInterval) || adInterval <= 0) {
		return false;
	}
	return isReelsFeedEnabled(configParams);
};

/**
 * Interleaves ad slots into the media list: with an interval of 3 the feed reads
 * video, video, video, ad, video, video, video, ad, ...
 */
export const buildStoryFeedItems = ({
	medias,
	adInterval,
	adsEnabled,
}: {
	medias: any[];
	adInterval: number;
	adsEnabled: boolean;
}): StoryFeedItem[] => {
	const items: StoryFeedItem[] = [];
	let adSlotIndex = 0;

	(medias ?? []).forEach((media: any, mediaIndex: number) => {
		if (adsEnabled && adInterval > 0 && mediaIndex > 0 && mediaIndex % adInterval === 0) {
			items.push({ type: STORY_AD_ITEM_TYPE, key: `story-ad-${adSlotIndex}`, adSlotIndex });
			adSlotIndex += 1;
		}
		items.push({
			type: STORY_VIDEO_ITEM_TYPE,
			key: `${media?.id}-${mediaIndex}`,
			media,
			mediaIndex,
		});
	});

	return items;
};

/** The mute param wins, otherwise playbackOptions. Mirrors PlayerHelper.createPlayerInstance. */
export const getStoryAdStartVolume = ({ settings, configParams }: { settings?: any; configParams?: any }): number => {
	const playbackOptions = settings?.playbackOptions ?? {};
	const defaultVolume = playbackOptions.setDefaultVolume ? (playbackOptions.defaultVolumeValue ?? 0) / 100 || 0.5 : 0.5;
	const paramMute = configParams?.mute;

	if (paramMute) {
		return paramMute === 'true' ? 0 : defaultVolume;
	}
	return playbackOptions.muteAtStart ? 0 : defaultVolume;
};

/** Both shapes: only a mounted media carries the flattened `images`. No frame means a black card. */
export const getStoryThumbnailUrl = (media?: any): string => {
	const fromMountedPlayer = media?.images?.[0]?.url;
	if (fromMountedPlayer) {
		return fromMountedPlayer;
	}

	const resources = media?.asset?.resources ?? media?.resources ?? [];
	const images = resources.filter((resource: any) => resource?.type === 'image');
	const thumbnail = images.find((image: any) => image?.rel?.includes('thumbnail')) ?? images[0];
	// Largest first, matching what the mounted path settles on.
	const renditions = [...(thumbnail?.renditions ?? [])].sort((a: any, b: any) => (b?.width ?? 0) - (a?.width ?? 0));
	return renditions[0]?.links?.[0]?.href ?? '';
};

export const STORY_AD_SKIP_AFTER_DEFAULT = 5;

/** Embed attribute, then preset, then default. A non-number is skipped, not read as 0. */
export const firstStoryAdNumber = (candidates: any[], fallback: number): number => {
	for (const candidate of candidates) {
		if (candidate === undefined || candidate === null || String(candidate).trim() === '') {
			continue;
		}
		const value = Number(candidate);
		if (Number.isFinite(value) && value >= 0) {
			return value;
		}
	}
	return fallback;
};

/**
 * A clip's title and description. Read from several shapes for the same reason the thumbnail is:
 * a mounted media is flattened, everything else in the feed is still the raw playlist entry.
 */
export const getStoryClipInfo = (media?: any): { title: string; description: string } => ({
	title: String(media?.metadata?.title ?? media?.mediaName ?? media?.name ?? media?.title ?? '').trim(),
	description: String(media?.metadata?.description ?? media?.description ?? '').trim(),
});

/** Ad slots shift the slide numbering, so a playlist position has to be translated. */
export const getFeedIndexForMediaIndex = (items: StoryFeedItem[], mediaIndex: number): number => {
	const found = items.findIndex((item) => item.type === STORY_VIDEO_ITEM_TYPE && item.mediaIndex === mediaIndex);
	return found === -1 ? 0 : found;
};

export const isStoryAdItem = (item?: StoryFeedItem): item is StoryFeedAdItem => item?.type === STORY_AD_ITEM_TYPE;

export const isStoryVideoItem = (item?: StoryFeedItem): item is StoryFeedVideoItem =>
	item?.type === STORY_VIDEO_ITEM_TYPE;
